import type { ApiErrorBody } from '@mervonn/shared-types';

/**
 * A failed API call, carrying API Specification §2.3's `error.code` so the
 * UI can map it to a specific message (UX Specification §7 — "every
 * user-facing error message should map to a specific error.code, not a
 * generic catch-all string") rather than showing a raw server string.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody['error'] | undefined, fallbackMessage: string) {
    super(body?.message ?? fallbackMessage);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code ?? codeForStatus(status);
    this.details = body?.details;
    this.requestId = body?.request_id;
  }

  /** The session is gone — refresh failed or no credentials. The auth
   * layer watches for this to bounce the user to the login screen. */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.code === 'UNAUTHENTICATED';
  }
}

/** A network-level failure (server unreachable, DNS, CORS) — distinct from
 * an HTTP error the server actually returned. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Could not reach the server. Check your connection and try again.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

function codeForStatus(status: number): string {
  switch (status) {
    case 400:
    case 422:
      return 'VALIDATION_FAILED';
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 423:
      return 'LOCKED';
    case 429:
      return 'RATE_LIMITED';
    default:
      return 'INTERNAL_ERROR';
  }
}
